import { lazy } from 'react';

import { basepath } from '@/constants';
import { createTenantApp } from '@/app/create-tenant-app';
import { logOut, ensureAuthenticated } from '@features/login';
import { useIsAuthenticated } from '@shared/auth/auth-state';
import { homeIssuesLoader } from '@/app/routing/home-loaders';
import { homeSearchSchema } from './home/home.schema';

// Matches the '/callback' redirect_uri Auth.hooks.tsx's useBasename()
// resolves to, computed without a hook since beforeLoad runs outside
// React's render context.
const callbackBasename = `${(basepath ?? '/').replace(/\/$/, '')}/callback`;

const LazyHome = lazy(() => import('./home/home'));

function useOnClickLogOut(): ((url?: string) => void) | undefined {
	const isAuthenticated = useIsAuthenticated();
	return isAuthenticated
		? (url?: string): void => {
				logOut(url || '/logout');
			}
		: undefined;
}

export const router = createTenantApp({
	home: {
		path: '/',
		protect: 'auth',
		component: () => <LazyHome onClickLogOut={useOnClickLogOut()} />,
		loader: homeIssuesLoader,
		validateSearch: homeSearchSchema,
	},
	routes: [
		'issue',
		'issueArticle',
		'drafts',
		'callback',
		'logout',
		'subscriptions',
		'subscriptionsSuccess',
		'preview',
		// The offline reader behind the AppMenu's Downloads entry
		// (`widgets/downloads-drawer`).
		'downloadedIssue',
		'downloadedIssueArticle',
	],
	// 'auth' = protect these with whatever this tenant authenticates with;
	// the wiring below says what that means per config.app.auth.type.
	protect: { issue: 'auth', issueArticle: 'auth' },
	auth: {
		// basic/magazine's adapter is built with requireCognitoAuthType: true --
		// its guard only means anything for a Cognito tenant, so it's declared
		// under 'cognito' rather than '*'.
		guards: { cognito: () => ensureAuthenticated(callbackBasename) },
		useRouteGate: useOnClickLogOut,
	},
	// A tenant still needs `config.app.downloadedIssues.enabled` to actually
	// see the downloads entry (@shared/ui/app-menu's own gating).
	appMenu: ['favorites', 'downloadedIssues', 'install', 'about'],
});
